import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Manages validation rules for a data field
export default class extends Controller {
  static targets = ["container", "template", "validationsInput", "rule"]
  static values = {
    fieldType: String
  }

  connect() {
    logger.log("Validation builder connected")
    this.updateFieldType(this.fieldTypeValue || 'DataField::TextField')
    
    // Initialize validations from existing rules (when editing)
    // This ensures the hidden input is synced with rendered validation rules
    if (this.hasValidationsInputTarget && this.ruleTargets.length > 0) {
      // Only collect if there are actual rules (not just the template)
      const actualRules = Array.from(this.ruleTargets).filter(rule => {
        const index = rule.getAttribute('data-index')
        return index && index !== 'NEW_RECORD'
      })
      
      if (actualRules.length > 0) {
        // Validations are already rendered, just sync the hidden input
        this.collectValidations()
      }
    }
  }

  addValidation(event) {
    event.preventDefault()
    event.stopPropagation()
    
    logger.log('Add validation clicked')
    
    const template = this.templateTarget
    const container = this.containerTarget
    const newIndex = Date.now() // Unique index
    
    if (!template || !container) {
      logger.error('Template or container not found', { template: !!template, container: !!container })
      return
    }
    
    // Clone template content (DocumentFragment)
    const newRuleFragment = template.content.cloneNode(true)
    
    // Get the first element (the card) from the fragment
    const newRule = newRuleFragment.firstElementChild
    
    if (!newRule) {
      logger.error('No element found in template')
      return
    }
    
    // Update the card's data-index attribute
    newRule.setAttribute('data-index', newIndex)
    
    // Update all elements with data-index, name, or id attributes
    const allElements = newRule.querySelectorAll('*')
    allElements.forEach(el => {
      const oldIndex = el.getAttribute('data-index')
      if (oldIndex === 'NEW_RECORD') {
        el.setAttribute('data-index', newIndex)
      }
      
      // Update IDs
      if (el.id && el.id.includes('NEW_RECORD')) {
        el.id = el.id.replace('NEW_RECORD', newIndex)
      }
      
      // Update name attributes
      if (el.name && el.name.includes('NEW_RECORD')) {
        el.name = el.name.replace('NEW_RECORD', newIndex)
      }
    })
    
    // Remove the empty state message if it exists
    const emptyState = container.querySelector('.text-muted.text-center')
    if (emptyState && emptyState.textContent.includes('אין כללי אימות')) {
      emptyState.remove()
    }
    
    // Insert before template (or append if template is not a sibling)
    if (template.parentElement === container) {
      container.insertBefore(newRule, template)
    } else {
      container.appendChild(newRule)
    }
    
    logger.log('Validation rule added', { newIndex, rulesCount: this.ruleTargets.length })
    
    // Update validations
    this.collectValidations()
    
    // Focus on the type select
    const typeSelect = newRule.querySelector('[data-action*="onTypeChange"]')
    if (typeSelect) {
      typeSelect.focus()
    }
  }

  removeValidation(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const index = event.currentTarget.getAttribute('data-index')
    const rule = this.containerTarget.querySelector(`[data-validation-builder-target="rule"][data-index="${index}"]`)
    
    if (rule) {
      rule.remove()
      this.collectValidations()
      
      // Show empty state if no rules remain
      const remainingRules = this.containerTarget.querySelectorAll('[data-validation-builder-target="rule"]:not([data-index="NEW_RECORD"])')
      if (remainingRules.length === 0) {
        const emptyState = document.createElement('div')
        emptyState.className = 'text-muted text-center py-3'
        emptyState.textContent = 'אין כללי אימות'
        this.containerTarget.appendChild(emptyState)
      }
    }
  }

  onTypeChange(event) {
    const index = event.currentTarget.getAttribute('data-index')
    const rule = event.currentTarget.closest('[data-validation-builder-target="rule"]')
    if (!rule) return
    
    // Find fields by name pattern (they may have different index formats)
    const valueField = rule.querySelector('[name*="validation_value"]')
    const flagsField = rule.querySelector('[name*="validation_flags"]')
    const messageField = rule.querySelector('[name*="validation_message"]')
    
    // Find containers (parent divs)
    const valueContainer = valueField?.parentElement
    const flagsContainer = flagsField?.parentElement
    const messageContainer = messageField?.parentElement
    
    // Find placeholder divs (siblings of the containers, not children)
    // The placeholder is in the same column (.col-md-6 or .col-md-2) as the inputs
    const valuePlaceholder = valueContainer?.parentElement?.querySelector('.bg-light.text-muted')
    const flagsPlaceholder = flagsContainer?.parentElement?.querySelector('.bg-light.text-muted')
    
    const type = event.target.value
    const valueTypes = ['min_length', 'max_length', 'min_value', 'max_value', 'pattern']
    const numericTypes = ['min_value', 'max_value']
    
    // Show/hide value field and placeholder based on type
    if (valueContainer) {
      if (valueTypes.includes(type)) {
        valueContainer.style.display = 'block'
        if (valuePlaceholder) valuePlaceholder.style.display = 'none'
        if (valueField) {
          // Don't set HTML5 required - server-side validation handles this
          valueField.removeAttribute('required')
          valueField.type = numericTypes.includes(type) ? 'number' : 'text'
          valueField.placeholder = type == 'pattern' ? 'Regex pattern' : (numericTypes.includes(type) ? 'Numeric value' : 'Value')
        }
        // Hide message field when showing value field
        if (messageContainer) messageContainer.style.display = 'none'
      } else if (type == 'custom_message') {
        valueContainer.style.display = 'none'
        if (valuePlaceholder) valuePlaceholder.style.display = 'none'
        if (messageContainer) messageContainer.style.display = 'block'
      } else {
        valueContainer.style.display = 'none'
        if (messageContainer) messageContainer.style.display = 'none'
        if (valuePlaceholder) {
          valuePlaceholder.style.display = 'block'
          valuePlaceholder.textContent = type == 'required' ? 'No additional value needed' : 'N/A'
        }
      }
    }
    
    // Show/hide flags field for pattern
    if (flagsContainer) {
      if (type == 'pattern') {
        flagsContainer.style.display = 'block'
        if (flagsPlaceholder) flagsPlaceholder.style.display = 'none'
      } else {
        flagsContainer.style.display = 'none'
        if (flagsPlaceholder) flagsPlaceholder.style.display = 'block'
      }
    }
    
    // Update validations when type changes
    this.collectValidations()
  }

  updateFieldType(fieldType) {
    this.fieldTypeValue = fieldType
    
    // Update available validation types based on field type
    this.ruleTargets.forEach(rule => {
      const typeSelect = rule.querySelector('[data-action*="onTypeChange"]')
      if (typeSelect) {
        // Filter options based on field type
        const options = Array.from(typeSelect.options)
        options.forEach(option => {
          const value = option.value
          let shouldShow = true
          
          if (value == 'min_value' || value == 'max_value') {
            shouldShow = fieldType == 'DataField::NumberField'
          } else if (value == 'email') {
            shouldShow = ['DataField::EmailField', 'DataField::TextField'].includes(fieldType)
          } else if (value == 'url') {
            shouldShow = fieldType == 'DataField::TextField'
          }
          
          option.style.display = shouldShow ? '' : 'none'
        })
      }
    })
  }

  collectValidations() {
    const validations = []
    
    this.ruleTargets.forEach(rule => {
      const index = rule.getAttribute('data-index')
      if (index == 'NEW_RECORD') return // Skip template
      
      // Find fields by name pattern (they may have different index formats)
      const typeSelect = rule.querySelector('[name*="validation_type"]')
      const valueField = rule.querySelector('[name*="validation_value"]')
      const flagsField = rule.querySelector('[name*="validation_flags"]')
      const messageField = rule.querySelector('[name*="validation_message"]')
      
      if (typeSelect && typeSelect.value) {
        let validationType = typeSelect.value
        
        // Convert 'pattern' to 'regex' for model compatibility
        if (validationType == 'pattern') {
          validationType = 'regex'
        }
        
        const validation = {
          type: validationType
        }
        
        // Handle regex/pattern validation separately (uses 'pattern' key, not 'value')
        if (validationType == 'regex') {
          if (valueField) {
            validation.pattern = valueField.value || ''
          }
          if (flagsField && flagsField.value) {
            validation.flags = flagsField.value
          }
        } else if (['min_length', 'max_length', 'min_value', 'max_value'].includes(validationType)) {
          // Always include value for validations that require it (even if empty, so server can validate)
          if (valueField) {
            const numericTypes = ['min_value', 'max_value']
            const fieldValue = valueField.value
            if (fieldValue && fieldValue.trim() !== '') {
              validation.value = numericTypes.includes(validationType)
                ? parseFloat(fieldValue) 
                : fieldValue
            } else {
              // Include empty value so server validation catches it
              validation.value = ''
            }
          }
        }
        
        // Custom message can be added to any validation type
        if (messageField && messageField.value) {
          validation.message = messageField.value
        }
        
        validations.push(validation)
      }
    })
    
    // Update hidden input
    if (this.hasValidationsInputTarget) {
      this.validationsInputTarget.value = JSON.stringify(validations)
      logger.log('Validations collected:', validations)
    }
    
    return validations
  }
}

